import groupBy from "ramda/es/groupBy";
import identity from "ramda/es/identity";
import last from "ramda/es/last";
import o from "ramda/es/o";
import prop from "ramda/es/prop";
import reduce from "ramda/es/reduce";
import values from "ramda/es/values";
import isNotEmpty from "ramda-extension/es/isNotEmpty";
import { from, merge, of, pipe } from 'rxjs';
import { bufferTime, catchError, mergeMap, filter as rxFilter, map as rxMap, share, startWith, takeUntil, withLatestFrom } from 'rxjs/operators';
import { createFieldRecord, metaFieldIndices, metaFieldName, metaValidationGroups } from '../../../utils/data';
import { runType } from '../../../validations/constants';
import { getFieldsToValidateFromDescriptor, getFieldsToValidateFromFormData } from '../../../validations/utils';
import { validateFieldsAsync } from '../../../validations/validateFields';
import { actionNames } from '../../transfer/control';
import { updateAsyncValidationState, updateValidationResultMultiple, validationRunner } from '../../transfer/transmission';
import { bufferControl, filterByActionType } from '../../utils';
import { getFieldsToValidate } from '../arrayRowValidations';
import { hasDependentFieldsWithAsyncValidations, mapDirtyFieldNames } from '../utils';
const mergeMapIdentity = mergeMap(identity);
const getActionValueByType = o(values, groupBy(prop('type')));
const mergeActionsPayload = reduce((acc, value)=>({
        ...acc,
        ...value,
        payload: {
            ...acc.payload,
            ...value.payload
        }
    }), {});
const getAsyncValidationStateActions = (fieldRecord)=>({
        start: updateAsyncValidationState({
            [fieldRecord]: {
                value: true
            }
        }),
        complete: updateAsyncValidationState({
            [fieldRecord]: {
                value: false
            }
        }),
        completeWithError: (originalError)=>updateAsyncValidationState({
                [fieldRecord]: {
                    value: false,
                    error: true,
                    originalError
                }
            })
    });
const singleSwitchMapByFieldRecordRunner = (mapFn)=>(input$)=>input$.pipe(mergeMap((value)=>{
            const fieldRecord = last(value[0]);
            const { complete, start, completeWithError } = getAsyncValidationStateActions(fieldRecord);
            return from(mapFn(value)).pipe(takeUntil(input$.pipe(rxFilter(([action])=>last(action) === fieldRecord))), rxMap((payload)=>[
                    updateValidationResultMultiple(payload, validationRunner.ASYNC),
                    complete
                ]), mergeMapIdentity, startWith(start), catchError((err)=>of(completeWithError(err))));
        }));
export default (({ control$, formDescriptor$, formData$, formFieldState$ }, reduxStore)=>{
    const blur$ = control$.pipe(filterByActionType(actionNames.BLUR, actionNames.VALIDATE_FIELD), rxMap((action)=>[
            action,
            [
                [
                    metaFieldName(action),
                    metaFieldIndices(action)
                ]
            ]
        ]));
    const arrayRow$ = control$.pipe(filterByActionType(actionNames.VALIDATE_ARRAY_ROW), withLatestFrom(formDescriptor$), rxMap(([action, formDescriptor])=>{
        const { arrayName, rowNo } = action.payload;
        return [
            action,
            getFieldsToValidate(arrayName, rowNo)(formDescriptor)
        ];
    }));
    const form$ = merge(control$.pipe(filterByActionType(actionNames.VALIDATE_FORM)), control$.pipe(filterByActionType(actionNames.RESET_FORM), rxFilter(({ meta })=>meta.validate))).pipe(withLatestFrom(formData$, formDescriptor$), rxMap(([action, formData, formDescriptor])=>[
            action,
            getFieldsToValidateFromDescriptor(formDescriptor, formData),
            {
                runType: runType.FORM,
                expandDependent: false
            }
        ]));
    const fields$ = control$.pipe(filterByActionType(actionNames.VALIDATE_FIELDS), rxMap((action)=>[
            action,
            action.payload
        ]));
    const formModification$ = control$.pipe(filterByActionType(actionNames.DEEP_MERGE_DATA, actionNames.REPLACE_DATA), rxFilter(({ meta })=>meta.validate), withLatestFrom(formDescriptor$), rxMap(([action, formDescriptor])=>[
            action,
            getFieldsToValidateFromFormData(formDescriptor, action.payload)
        ]));
    const register$ = control$.pipe(bufferControl(control$, actionNames.REGISTER), rxMap((action)=>[
            action,
            mapDirtyFieldNames(action)
        ]));
    return merge(blur$, arrayRow$, form$, formModification$, register$, fields$).pipe(pipe(rxFilter(([, fieldsToValidate])=>isNotEmpty(fieldsToValidate)), rxMap(([action, fieldsToValidate, validationParams])=>fieldsToValidate.map((fieldToValidate)=>[
                action,
                fieldToValidate,
                validationParams,
                createFieldRecord(fieldToValidate[0], fieldToValidate[1])
            ])), mergeMapIdentity, withLatestFrom(formDescriptor$, formFieldState$, formData$), rxFilter(([[, [fieldName], , fieldRecord], formDescriptor, formFieldstate])=>!!formFieldstate[fieldRecord] && (!!formDescriptor[fieldName]?.asyncValidations || hasDependentFieldsWithAsyncValidations(fieldName, formDescriptor))), singleSwitchMapByFieldRecordRunner(([[action, fieldToValidate, validationParams = {}], formDescriptor, formFieldState, formData])=>validateFieldsAsync({
            formData,
            reduxStore,
            formFieldState,
            formDescriptor,
            runType: runType.FIELD,
            groups: metaValidationGroups(action),
            fieldsToValidate: [
                fieldToValidate
            ],
            ...validationParams
        })), bufferTime(100), rxFilter(isNotEmpty), rxMap(getActionValueByType), mergeMapIdentity, rxMap(mergeActionsPayload)), share());
});
